import React from 'react';
import styled from 'styled-components';

// ─── Shared data (mirrors UserProfileModal.js options) ────────────────────────

export const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

export const GENDER_OPTIONS = [
  { value: 'male',       label: 'Male' },
  { value: 'female',     label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

export const EMPLOYMENT_OPTIONS = [
  { value: 'employed_full', label: 'Employed full-time' },
  { value: 'employed_part', label: 'Employed part-time' },
  { value: 'self_employed',  label: 'Self-employed' },
  { value: 'not_employed',  label: 'Not currently employed' },
  { value: 'retired',       label: 'Retired' },
  { value: 'student',       label: 'Student' },
  { value: 'other',         label: 'Other' },
];

export const INCOME_OPTIONS = [
  { value: 'under_30k',  label: 'Under $30,000' },
  { value: '30k_60k',    label: '$30,000 – $60,000' },
  { value: '60k_100k',   label: '$60,000 – $100,000' },
  { value: '100k_150k',  label: '$100,000 – $150,000' },
  { value: 'over_150k',  label: 'Over $150,000' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

const CURRENT_YEAR = new Date().getFullYear();
const AGE_OPTIONS = Array.from({ length: 75 }, (_, i) => {
  const birthYear = CURRENT_YEAR - 18 - i; // 18 to 92
  const age = CURRENT_YEAR - birthYear;
  return { value: birthYear, label: `${age} (born ${birthYear})` };
});

// ─── Styled components ────────────────────────────────────────────────────────

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
`;

const CheckGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CheckChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${p => p.$checked ? 'rgba(255,71,87,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${p => p.$checked ? 'rgba(255,71,87,0.5)' : 'rgba(255,255,255,0.1)'};
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.82rem;
  color: ${p => p.$checked ? '#ff8c96' : 'rgba(255,255,255,0.6)'};
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  &:hover { border-color: rgba(255,71,87,0.4); color: white; }
  input { display: none; }
`;

const AllToggle = styled.button`
  font-size: 0.77rem;
  font-weight: 600;
  color: ${p => p.$active ? '#ff4757' : 'rgba(255,255,255,0.35)'};
  background: none;
  border: 1px dashed ${p => p.$active ? 'rgba(255,71,87,0.4)' : 'rgba(255,255,255,0.12)'};
  border-radius: 20px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover { color: #ff4757; border-color: rgba(255,71,87,0.4); }
`;

const AgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AgeSelect = styled.select`
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  padding: 8px 10px;
  outline: none;
  cursor: pointer;
  flex: 1;
  &:focus { border-color: #ff4757; }
  option { background: #1a1a2e; }
`;

const AgeLabel = styled.span`
  font-size: 0.82rem;
  color: rgba(255,255,255,0.4);
  white-space: nowrap;
`;

const StateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 160px;
  overflow-y: auto;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
`;

const StateChip = styled.button`
  background: ${p => p.$selected ? 'rgba(255,71,87,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${p => p.$selected ? 'rgba(255,71,87,0.5)' : 'rgba(255,255,255,0.08)'};
  border-radius: 6px;
  color: ${p => p.$selected ? '#ff8c96' : 'rgba(255,255,255,0.5)'};
  font-size: 0.75rem;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.12s;
  &:hover { border-color: rgba(255,71,87,0.4); color: white; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toggleValue(arr, val) {
  return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
}

function isAll(arr) {
  return !arr || arr.length === 0 || arr.includes('all');
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * AgencyTargetingPanel
 *
 * Props:
 *   value: {
 *     target_genders: string[],
 *     target_birth_year_min: int|null,
 *     target_birth_year_max: int|null,
 *     target_states: string[],
 *     target_employment_statuses: string[],
 *     target_income_ranges: string[],
 *   }
 *   onChange: (updatedValue) => void
 */
const AgencyTargetingPanel = ({ value = {}, onChange }) => {
  const {
    target_genders = [],
    target_birth_year_min = null,
    target_birth_year_max = null,
    target_states = [],
    target_employment_statuses = [],
    target_income_ranges = [],
  } = value;

  const update = (patch) => onChange({ ...value, ...patch });

  // Gender
  const allGenders = isAll(target_genders);
  const toggleGender = (v) => {
    update({ target_genders: toggleValue(target_genders.filter(x => x !== 'all'), v) });
  };

  // State
  const allStates = isAll(target_states);
  const toggleState = (code) => {
    update({ target_states: toggleValue(target_states.filter(x => x !== 'all'), code) });
  };

  // Employment
  const allEmployment = isAll(target_employment_statuses);
  const toggleEmployment = (v) => {
    update({ target_employment_statuses: toggleValue(target_employment_statuses.filter(x => x !== 'all'), v) });
  };

  // Income
  const allIncome = isAll(target_income_ranges);
  const toggleIncome = (v) => {
    update({ target_income_ranges: toggleValue(target_income_ranges.filter(x => x !== 'all'), v) });
  };

  return (
    <Panel>
      {/* Gender */}
      <Section>
        <SectionTitle>Gender</SectionTitle>
        <CheckGrid>
          <AllToggle
            type="button"
            $active={allGenders}
            onClick={() => update({ target_genders: [] })}
          >
            All genders
          </AllToggle>
          {GENDER_OPTIONS.map(opt => (
            <CheckChip key={opt.value} $checked={!allGenders && target_genders.includes(opt.value)}>
              <input
                type="checkbox"
                checked={!allGenders && target_genders.includes(opt.value)}
                onChange={() => toggleGender(opt.value)}
              />
              {opt.label}
            </CheckChip>
          ))}
        </CheckGrid>
      </Section>

      {/* Age range */}
      <Section>
        <SectionTitle>Age range</SectionTitle>
        <AgeRow>
          <AgeLabel>Min age</AgeLabel>
          <AgeSelect
            value={target_birth_year_min ?? ''}
            onChange={e => update({ target_birth_year_min: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">No minimum</option>
            {AGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </AgeSelect>
          <AgeLabel>Max age</AgeLabel>
          <AgeSelect
            value={target_birth_year_max ?? ''}
            onChange={e => update({ target_birth_year_max: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">No maximum</option>
            {AGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </AgeSelect>
        </AgeRow>
      </Section>

      {/* State */}
      <Section>
        <SectionTitle>State</SectionTitle>
        <div style={{ marginBottom: 8 }}>
          <AllToggle
            type="button"
            $active={allStates}
            onClick={() => update({ target_states: [] })}
          >
            All states
          </AllToggle>
        </div>
        <StateGrid>
          {US_STATES.map(([code, name]) => (
            <StateChip
              key={code}
              type="button"
              $selected={!allStates && target_states.includes(code)}
              onClick={() => toggleState(code)}
              title={name}
            >
              {code}
            </StateChip>
          ))}
        </StateGrid>
      </Section>

      {/* Employment */}
      <Section>
        <SectionTitle>Employment status</SectionTitle>
        <CheckGrid>
          <AllToggle
            type="button"
            $active={allEmployment}
            onClick={() => update({ target_employment_statuses: [] })}
          >
            All statuses
          </AllToggle>
          {EMPLOYMENT_OPTIONS.map(opt => (
            <CheckChip key={opt.value} $checked={!allEmployment && target_employment_statuses.includes(opt.value)}>
              <input
                type="checkbox"
                checked={!allEmployment && target_employment_statuses.includes(opt.value)}
                onChange={() => toggleEmployment(opt.value)}
              />
              {opt.label}
            </CheckChip>
          ))}
        </CheckGrid>
      </Section>

      {/* Income */}
      <Section>
        <SectionTitle>Income range</SectionTitle>
        <CheckGrid>
          <AllToggle
            type="button"
            $active={allIncome}
            onClick={() => update({ target_income_ranges: [] })}
          >
            All incomes
          </AllToggle>
          {INCOME_OPTIONS.map(opt => (
            <CheckChip key={opt.value} $checked={!allIncome && target_income_ranges.includes(opt.value)}>
              <input
                type="checkbox"
                checked={!allIncome && target_income_ranges.includes(opt.value)}
                onChange={() => toggleIncome(opt.value)}
              />
              {opt.label}
            </CheckChip>
          ))}
        </CheckGrid>
      </Section>
    </Panel>
  );
};

export default AgencyTargetingPanel;
